import Icon from 'components/_UI/Icon';
import Text from 'components/_UI/Text';
import date from 'helpers/date';
import useFavorite from 'hooks/useFavorite';
import React from 'react';
import { Calendar, Heart, MapPin } from 'lucide-react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import userStatus from 'theme/userStatus';

export default function EventCardV2({
    data,
    withoutTags = false,
    onEventClick,
    expired = false,
}) {
    const { isFavorited, toggleFavorite } = useFavorite(data?.slug, data?.user_status === userStatus.FAVORITE, data);

    const eventDate = data?.dates?.[0]?.date ? date.format(data.dates[0].date) : 'Data a definir';
    const eventTime = data?.dates?.[0]?.start ? date.formatTime(data.dates[0].start) : '--:--';

    const addressText = data?.address?.street
        ? `${data.address.street.name}, Bairro ${data.address.street.district}`
        : data?.address?.raw_address || null;

    return (
        <CardContainer
            id='event_card'
            onClick={() => onEventClick(data)}
            expired={expired}
        >
            <ImageWrapper>
                <CardImage
                    src={data?.images?.length && data?.images[0] || data.path || '/assets/images/placeholder-events.png'}
                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                />

                <TopTypesContainer>
                    {(() => {
                        const isFree = data?.classification?.name === eventTypes.free.slug;
                        return (
                            <Tag isType={isFree ? 'free' : 'paid'}>
                                <Text name='mini' color={colors.neutralLight} paddingBottom={0}>
                                    {isFree ? 'Gratuito' : 'Pago'}
                                </Text>
                            </Tag>
                        );
                    })()}

                    {data?.trending && (
                        <Tag isTrend>
                            <Column width='16px' height='16px'>
                                <Icon name='fire-filled' color={colors.occasionalPink} />
                            </Column>
                            <Text
                                name='mini'
                                color={colors.occasionalPink}
                                paddingBottom={0}
                                marginLeft={'4px'}
                            >
                                Em alta
                            </Text>
                        </Tag>
                    )}
                </TopTypesContainer>

                <FavoriteContent onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite();
                }}>
                    <Heart
                        size={14}
                        color={isFavorited ? colors.occasionalPink : colors.nightLight}
                        fill={isFavorited ? colors.occasionalPink : 'none'}
                    />
                </FavoriteContent>
            </ImageWrapper>

            <CardContent>
                <Text type='bold' color={colors.nightDark} ellipsisAt='95%' paddingBottom={0}>
                    {data.name}
                </Text>

                <Row verticalCenter>
                    <Calendar size={13} color={colors.nightLight} />
                    <Text name='small' color={colors.nightLight} paddingBottom={0} marginLeft='4px'>
                        {eventDate} · {eventTime}
                    </Text>
                </Row>

                <Row verticalCenter>
                    <MapPin size={13} color={colors.nightLight} />
                    <Text name='small' color={colors.nightLight} ellipsisAt='95%' paddingBottom={0} marginLeft='4px'>
                        {addressText || 'Local a definir'}
                    </Text>
                </Row>

                {!withoutTags && data?.tags?.length > 0 && (
                    <TagsContainer>
                        <Text name='mini' ellipsisAt='95%' paddingBottom='0px' marginLeft='3px'>
                            {data?.tags?.map(item => `#${item.name} `)}
                        </Text>
                    </TagsContainer>
                )}
            </CardContent>
        </CardContainer>
    )
};

const CardContainer = styled(Column)`
    width: 100%;
    cursor: pointer;
    position: relative;
    background: transparent;

    ${({ expired }) => expired && `opacity: 0.6;`}

    &:hover img {
        transform: translateY(-2px);
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 2;
    border-radius: 12px;
    overflow: hidden;
`;

const CardImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    border-radius: 12px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
`;

const TopTypesContainer = styled(Row)`
    position: absolute;
    top: 10px;
    left: 8px;
    width: auto;
`;

const Tag = styled(FullyCentered)`
    flex-direction: row;
    padding: 4px 8px;
    border-radius: 6px;
    margin-right: 4px;
    width: auto;

    &:last-child {
        margin-right: 0;
    }

    background: ${({ isType, isTrend }) =>
        isTrend ? 'rgba(255, 255, 255, 0.92)' :
        isType === 'free' ? 'rgba(43, 200, 102, 0.85)' :
        isType === 'paid' ? 'rgba(159, 22, 219, 0.85)' :
        'rgba(255, 255, 255, 0.92)'
    };
    border: none;

    ${({ isTrend }) => isTrend && `
        border: 1px solid ${colors.occasionalPink}40;
    `}
`;

const FavoriteContent = styled(FullyCentered)`
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    cursor: pointer;
`;

const CardContent = styled(Column)`
    padding: 8px 4px 4px;
    gap: 4px;
`;

const TagsContainer = styled(Row)`
    align-items: center;
    margin-top: 4px;
`;
